import { MakeLogicType, actions, afterMount, connect, kea, key, path, props, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { subscriptions } from 'kea-subscriptions'

import { FEATURE_FLAGS } from 'lib/constants'
import { dayjs } from 'lib/dayjs'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { teamLogic } from 'scenes/teamLogic'

import { experimentsSessionContextRetrieve } from 'products/experiments/frontend/generated/api'
import type {
    ExperimentSessionContextItemApi,
    ExperimentSessionContextResponseApi,
} from 'products/experiments/frontend/generated/api.schemas'

import type { FeatureFlagsSet } from '../../../../lib/logic/featureFlagLogic'

export interface SessionRecordingExperimentContextLogicProps {
    sessionRecordingId: string
}

export function isControlVariant(item: ExperimentSessionContextItemApi): boolean {
    return item.variant.toLowerCase() === 'control'
}

// Lower rank sorts first: multi-variant (a bias signal) → a non-control variant the session saw → control.
export function experimentSignalRank(item: ExperimentSessionContextItemApi): number {
    if (item.multiple_variants) {
        return 0
    }
    return isControlVariant(item) ? 2 : 1
}

// Parsed rather than string-compared: ordering by exposure is only correct if two timestamps written in
// different shapes (offset, sub-second precision) still compare as the instants they represent.
export function exposureMs(item: ExperimentSessionContextItemApi): number {
    return item.first_exposure_timestamp ? dayjs(item.first_exposure_timestamp).valueOf() : 0
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sessionRecordingExperimentContextLogicValues {
    featureFlags: FeatureFlagsSet // featureFlagLogic
    currentProjectId: number | string // teamLogic
    enrolledCount: number
    enrolledItems: ExperimentSessionContextItemApi[]
    expandedExperimentIds: number[]
    experimentContext: ExperimentSessionContextResponseApi | null
    experimentContextEnabled: boolean
    experimentContextLoading: boolean
    experimentItems: ExperimentSessionContextItemApi[]
    hasExperimentContext: boolean
    hasMultipleVariantWarning: boolean
    seenCount: number
    seenItems: ExperimentSessionContextItemApi[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sessionRecordingExperimentContextLogicActions {
    loadExperimentContext: () => any
    loadExperimentContextFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadExperimentContextSuccess: (
        experimentContext: ExperimentSessionContextResponseApi | null,
        payload?: any
    ) => {
        experimentContext: ExperimentSessionContextResponseApi | null
        payload?: any
    }
    setExperimentExpanded: (
        experimentId: number,
        expanded: boolean
    ) => {
        expanded: boolean
        experimentId: number
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sessionRecordingExperimentContextLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        experimentContextEnabled: (featureFlags: FeatureFlagsSet, arg: any) => boolean
        experimentItems: (
            experimentContext: ExperimentSessionContextResponseApi | null
        ) => ExperimentSessionContextItemApi[]
        seenItems: (experimentItems: ExperimentSessionContextItemApi[]) => ExperimentSessionContextItemApi[]
        enrolledItems: (experimentItems: ExperimentSessionContextItemApi[]) => ExperimentSessionContextItemApi[]
        seenCount: (seenItems: ExperimentSessionContextItemApi[]) => number
        enrolledCount: (enrolledItems: ExperimentSessionContextItemApi[]) => number
        hasExperimentContext: (experimentItems: ExperimentSessionContextItemApi[]) => boolean
        hasMultipleVariantWarning: (seenItems: ExperimentSessionContextItemApi[]) => boolean
    }
}

export type sessionRecordingExperimentContextLogicType = MakeLogicType<
    sessionRecordingExperimentContextLogicValues,
    sessionRecordingExperimentContextLogicActions,
    SessionRecordingExperimentContextLogicProps,
    sessionRecordingExperimentContextLogicMeta
>

export const sessionRecordingExperimentContextLogic = kea<sessionRecordingExperimentContextLogicType>([
    path((key) => [
        'scenes',
        'session-recordings',
        'player',
        'player-meta',
        'sessionRecordingExperimentContextLogic',
        key,
    ]),
    props({} as SessionRecordingExperimentContextLogicProps),
    key((props: SessionRecordingExperimentContextLogicProps) => props.sessionRecordingId),
    connect(() => ({
        values: [featureFlagLogic, ['featureFlags'], teamLogic, ['currentProjectId']],
    })),
    actions({
        // Whether an experiment's metric events are expanded in the sidebar. Collapsed by default —
        // the row shows only exposure + variant until the viewer opts in, mirroring the inspector.
        setExperimentExpanded: (experimentId: number, expanded: boolean) => ({ experimentId, expanded }),
    }),
    reducers({
        expandedExperimentIds: [
            [] as number[],
            {
                setExperimentExpanded: (
                    state: number[],
                    { experimentId, expanded }: { experimentId: number; expanded: boolean }
                ) => (expanded ? [...state, experimentId] : state.filter((id) => id !== experimentId)),
            },
        ],
    }),
    loaders(({ props, values }) => ({
        experimentContext: [
            null as ExperimentSessionContextResponseApi | null,
            {
                loadExperimentContext: async () => {
                    if (!values.featureFlags[FEATURE_FLAGS.REPLAY_EXPERIMENT_CONTEXT] || !props.sessionRecordingId) {
                        return null
                    }
                    try {
                        return await experimentsSessionContextRetrieve(String(values.currentProjectId), {
                            session_id: props.sessionRecordingId,
                        })
                    } catch {
                        // A 404 just means the recording has no queryable metadata (yet) — show nothing.
                        return null
                    }
                },
            },
        ],
    })),
    selectors({
        experimentContextEnabled: [
            (s) => [s.featureFlags, (_, props) => props.sessionRecordingId],
            (featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet, sessionRecordingId): boolean =>
                !!featureFlags[FEATURE_FLAGS.REPLAY_EXPERIMENT_CONTEXT] && !!sessionRecordingId,
        ],
        experimentItems: [
            (s) => [s.experimentContext],
            (experimentContext: ExperimentSessionContextResponseApi | null): ExperimentSessionContextItemApi[] =>
                experimentContext?.results ?? [],
        ],
        // Experiments with an exposure event during this recording — these have a moment on the timeline to jump to.
        // Ordered by when the exposure happens so the list reads along the playback timeline. Exposures that
        // share a timestamp (typically several flags evaluated together at the very start) fall back to signal
        // rank, then name, so the order stays stable rather than depending on backend result order.
        seenItems: [
            (s) => [s.experimentItems],
            (experimentItems: ExperimentSessionContextItemApi[]): ExperimentSessionContextItemApi[] =>
                experimentItems
                    .filter((item) => item.first_exposure_timestamp != null)
                    .sort(
                        (a, b) =>
                            exposureMs(a) - exposureMs(b) ||
                            experimentSignalRank(a) - experimentSignalRank(b) ||
                            a.experiment_name.localeCompare(b.experiment_name)
                    ),
        ],
        // Experiments the person is enrolled in but with no exposure event in this session — the variant
        // is only known from stamped flag properties.
        enrolledItems: [
            (s) => [s.experimentItems],
            (experimentItems: ExperimentSessionContextItemApi[]): ExperimentSessionContextItemApi[] =>
                experimentItems
                    .filter((item) => item.first_exposure_timestamp == null)
                    .sort(
                        (a, b) =>
                            experimentSignalRank(a) - experimentSignalRank(b) ||
                            a.experiment_name.localeCompare(b.experiment_name)
                    ),
        ],
        seenCount: [(s) => [s.seenItems], (seenItems: ExperimentSessionContextItemApi[]): number => seenItems.length],
        enrolledCount: [
            (s) => [s.enrolledItems],
            (enrolledItems: ExperimentSessionContextItemApi[]): number => enrolledItems.length,
        ],
        hasExperimentContext: [
            (s) => [s.experimentItems],
            (experimentItems: ExperimentSessionContextItemApi[]): boolean => experimentItems.length > 0,
        ],
        // Scoped to seenItems: this warns the chip that summarizes only in-recording experiments, so an
        // enrollment without an in-session exposure that saw multiple variants must not trigger the warning.
        hasMultipleVariantWarning: [
            (s) => [s.seenItems],
            (seenItems: ExperimentSessionContextItemApi[]): boolean => seenItems.some((item) => item.multiple_variants),
        ],
    }),
    // Feature flags can arrive after the player mounts (posthog-js loads them asynchronously),
    // so re-attempt the load when the gate flips on rather than only once at mount.
    subscriptions(({ actions, values }) => ({
        experimentContextEnabled: (enabled: boolean) => {
            if (enabled && !values.experimentContext && !values.experimentContextLoading) {
                actions.loadExperimentContext()
            }
        },
    })),
    afterMount(({ actions, values }) => {
        if (!values.experimentContextLoading) {
            actions.loadExperimentContext()
        }
    }),
])
